import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Router} from '@angular/router';
import {NbToastrService} from '@nebular/theme';
import {CookieService} from 'ngx-cookie-service';
import {TranslateService} from '@ngx-translate/core';
import {APIService} from './api.service';
import {Quote, QuoteListItem, QuoteSettings} from '../interfaces/quote';
import {UserRef} from '../interfaces/user';

@Injectable({providedIn: 'root'})
export class QuoteService extends APIService {
  constructor(http: HttpClient, toast: NbToastrService, router: Router, cookie: CookieService,
              private translate: TranslateService) {
    super(http, toast, router, cookie);
  }

  private notice(key: string): string {
    return this.translate.instant('quotes.notice.' + key);
  }

  async getSettings(): Promise<QuoteSettings> {
    return await this.get<QuoteSettings>('/quotes/settings', this.notice('get_settings'));
  }

  async saveSettings(settings: QuoteSettings): Promise<QuoteSettings> {
    return await this.put<QuoteSettings, QuoteSettings>('/quotes/settings', settings, this.notice('settings_saved'));
  }

  async list(opportunityId: number): Promise<QuoteListItem[]> {
    return (await this.get<{data: QuoteListItem[]}>(`/opportunities/${opportunityId}/quotes`, this.notice('get_quotes'))).data;
  }

  async create(opportunityId: number): Promise<Quote> {
    return await this.post<object, Quote>(`/opportunities/${opportunityId}/quotes`, {});
  }

  // Active members of the company: who may be responsible or on the quote.
  async members(): Promise<UserRef[]> {
    return (await this.get<UserRef[]>('/quotes/members', null)) ?? [];
  }

  async getQuote(id: number): Promise<Quote> {
    return await this.get<Quote>('/quotes/' + id, this.notice('get_quote'));
  }

  async save(id: number, quote: Partial<Quote>): Promise<Quote> {
    return await this.put<Partial<Quote>, Quote>('/quotes/' + id, quote, this.notice('saved'));
  }

  async remove(id: number): Promise<void> {
    await this.delete('/quotes/' + id);
  }

  // Rendered from the stored quote, so the editor saves first. A refused PDF rejects with {reason}, for the page to show.
  async downloadPdf(id: number): Promise<void> {
    const response = await this.get<any>('/quotes/' + id + '/pdf', this.notice('get_pdf'), {responseType: 'blob', observe: 'response'}, [422])
      .catch(async e => { throw e?.status === 422 ? {reason: await this.refusal(e.error)} : e; });
    const a = document.createElement('a');
    a.href = window.URL.createObjectURL(response.body);
    a.download = response.headers.get('Content-Disposition')?.match(/filename="?([^";]+)"?/)?.[1] || 'quote.pdf';
    a.click();
    window.URL.revokeObjectURL(a.href);
  }

  // The refusal comes as a Blob, like the PDF would; its message in the app's language where it has a text.
  private async refusal(body: Blob): Promise<string> {
    const message: string = await body.text().then(t => JSON.parse(t).message).catch(() => null);
    if (message?.startsWith('No hours or rate for: ')) {
      return this.translate.instant('quotes.pdf_needs_hours', {names: message.slice(22).replace(/\.$/, '')});
    }
    return message?.includes('file://') ? this.translate.instant('quotes.pdf_file_link') : message || this.notice('get_pdf');
  }
}
